import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { MfaQrCode } from '@/components/MfaQrCode';
import { MFA_ISSUER } from '@/lib/mfa-branding';
import { Loader2, ShieldCheck, Copy, CheckCircle2 } from 'lucide-react';
import posLogo from '@/assets/posvision-logo.png';

interface MfaEnrollProps {
  onComplete: () => void;
  onSkip?: () => void;
  mandatory?: boolean;
}

export default function MfaEnroll({ onComplete, onSkip, mandatory = false }: MfaEnrollProps) {
  const [otpUri, setOtpUri] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    enrollFactor();
  }, []);

  const enrollFactor = async () => {
    setLoading(true);
    try {
      // Remove any existing unverified factors first
      const { data: existing } = await supabase.auth.mfa.listFactors();
      if (existing) {
        for (const f of existing.totp.filter((f) => f.status !== 'verified')) {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        }
      }
      const friendlyName = `VisionPos 360 ${Date.now()}`;
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        issuer: MFA_ISSUER,
        friendlyName,
      });
      if (error) throw error;
      setOtpUri(data.totp.uri);
      setSecret(data.totp.secret);
      setFactorId(data.id);
    } catch (err: any) {
      toast.error('Erreur lors de la configuration 2FA: ' + (err.message || ''));
    }
    setLoading(false);
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error('Veuillez entrer un code à 6 chiffres');
      return;
    }
    setVerifying(true);
    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyError) throw verifyError;

      toast.success('Authentification double facteur activée !');
      onComplete();
    } catch (err: any) {
      toast.error('Code invalide. Veuillez réessayer.');
      setCode('');
    }
    setVerifying(false);
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[hsl(224,50%,14%)] to-[hsl(224,55%,8%)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[hsl(224,50%,14%)] to-[hsl(224,55%,8%)] px-4">
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardContent className="space-y-5 p-8">
          <div className="flex flex-col items-center gap-3">
            <img src={posLogo} alt="VisionPos 360" className="h-10 object-contain" />
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <ShieldCheck className="h-7 w-7 text-primary" />
            </div>
            <div className="space-y-1 text-center">
              <h2 className="text-lg font-bold text-foreground">
                Configurer l'authentification 2FA
              </h2>
              <p className="text-xs text-muted-foreground">
                {mandatory
                  ? 'La double authentification est obligatoire pour votre rôle.'
                  : 'Renforcez la sécurité de votre compte.'}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium">1. Scannez ce QR code avec votre application TOTP</p>
            <p className="text-[10px] text-muted-foreground">
              Google Authenticator, Authy, Microsoft Authenticator…
            </p>
            {otpUri && (
              <div className="flex justify-center py-2">
                <MfaQrCode uri={otpUri} size={192} />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Ou entrez manuellement cette clé :</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded-md bg-muted px-3 py-2 font-mono text-xs tracking-wider">
                {secret}
              </code>
              <Button variant="ghost" size="sm" onClick={handleCopySecret} className="shrink-0">
                {copied ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>2. Entrez le code à 6 chiffres</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="text-center font-mono text-2xl tracking-[0.5em]"
              maxLength={6}
              autoFocus
              data-allow-autofocus=""
              onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Button
              onClick={handleVerify}
              disabled={verifying || code.length !== 6}
              className="w-full gap-2"
            >
              {verifying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ShieldCheck className="h-4 w-4" />
              )}
              Activer la 2FA
            </Button>
            {!mandatory && onSkip && (
              <Button variant="ghost" onClick={onSkip} className="text-xs text-muted-foreground">
                Passer pour le moment
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
